import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { Button } from '../ui/button';
import { 
  Truck, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  Star,
  Route,
  FileText
} from 'lucide-react';
import { EnhancedTransporter } from '../../lib/api';

interface TransporterDashboardProps {
  transporter: EnhancedTransporter;
  onEdit?: () => void;
  onVerify?: () => void;
  onReject?: () => void;
}

export default function TransporterDashboard({ 
  transporter, 
  onEdit, 
  onVerify, 
  onReject 
}: TransporterDashboardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getVerificationBadge = () => {
    return <StatusBadge status={transporter.verificationStatus} />;
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Truck className="h-8 w-8 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{transporter.name}</CardTitle>
                <p className="text-muted-foreground">{transporter.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={transporter.isActive ? 'active' : 'inactive'} />
              {getVerificationBadge()}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rating</p>
                <p className="text-2xl font-bold">{transporter.rating || 'N/A'}</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed Trips</p>
                <p className="text-2xl font-bold">{transporter.completedTrips || 0}</p>
              </div>
              <Route className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Vehicles</p>
                <p className="text-2xl font-bold">{transporter.vehicles?.length || 0}</p>
              </div>
              <Truck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Documents</p>
                <p className="text-2xl font-bold">{transporter.documents?.length || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{transporter.email}</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{transporter.mobile}</span>
            </div>
            {transporter.alternatePhone && (
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{transporter.alternatePhone} (Alternate)</span>
              </div>
            )}
            <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Joined {formatDate(transporter.createdAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Address
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <p>{transporter.address.street}</p>
              <p>{transporter.address.city}, {transporter.address.state}</p>
              <p>{transporter.address.pincode}</p>
              <p>{transporter.address.country}</p>
            </div>
          </CardContent>
        </Card>

        {/* License Information */}
        {transporter.drivingLicenseNumber && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                License Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">License Number</p>
                <p>{transporter.drivingLicenseNumber}</p>
              </div>
              {transporter.drivingLicenseExpiry && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expiry Date</p>
                  <p>{formatDate(transporter.drivingLicenseExpiry)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {onEdit && (
              <Button onClick={onEdit} className="w-full">
                Edit Transporter
              </Button>
            )}
            {transporter.verificationStatus === 'pending' && (
              <div className="flex space-x-2">
                {onVerify && (
                  <Button onClick={onVerify} className="flex-1 bg-green-600 hover:bg-green-700">
                    Verify
                  </Button>
                )}
                {onReject && (
                  <Button onClick={onReject} variant="destructive" className="flex-1">
                    Reject
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rejection Reason */}
      {transporter.verificationStatus === 'rejected' && transporter.rejectionReason && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Rejection Reason
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">{transporter.rejectionReason}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}